import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
  type ISourceEntity,
} from '@principle-theorem/practice-migrations/interfaces';
import { TypeGuard, type Timezone } from '@principle-theorem/shared';
import { flow, isBoolean, isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import { OFFSET_PLACEHOLDER } from '../../../source/source-helpers';

export const PATIENT_MEDICAL_ALLERGY_RESOURCE_TYPE = 'patientMedicalAllergy';

export const PATIENT_MEDICAL_ALLERGY_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Patient Medical Allergy List',
      description: '',
      idPrefix: PATIENT_MEDICAL_ALLERGY_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

export interface IPraktikaMedicalAllergy {
  patient_id: string;
  item_id: string;
  is_present: boolean;
  value: string | null;
}

export interface IPraktikaMedicalAllergyFilters {
  patientId: string;
}

function isPraktikaMedicalAllergy(
  item: unknown,
  logError: boolean
): item is IPraktikaMedicalAllergy {
  return TypeGuard.interface<IPraktikaMedicalAllergy>(
    {
      patient_id: isString,
      item_id: isString,
      is_present: isBoolean,
      value: TypeGuard.nilOr(isString),
    },
    logError
  )(item);
}

export type PraktikaMedicalAllergy = ISourceEntityTypes<
  IPraktikaMedicalAllergy,
  object,
  IPraktikaMedicalAllergyFilters
>;

/**
 * Rows where bpresent=false AND vchvalue is blank are skipped at query level
 * to avoid migrating pure "answered no" noise.
 */
const PATIENT_MEDICAL_ALLERGY_SOURCE_QUERY = `
SELECT
  ipatientnumber::TEXT AS patient_id,
  vchitemid AS item_id,
  COALESCE(bpresent, false) AS is_present,
  NULLIF(TRIM(vchvalue), '') AS value
FROM patient_medical_allergy
WHERE ipatientnumber IS NOT NULL
  AND (bpresent = true OR NULLIF(TRIM(vchvalue), '') IS NOT NULL)
ORDER BY ipatientnumber, vchitemid
${OFFSET_PLACEHOLDER}
`;

const PATIENT_MEDICAL_ALLERGY_ESTIMATE_QUERY = `
SELECT
  ipatientnumber::TEXT AS patient_id,
  vchitemid AS item_id,
  COALESCE(bpresent, false) AS is_present,
  NULLIF(TRIM(vchvalue), '') AS value
FROM patient_medical_allergy
WHERE ipatientnumber IS NOT NULL
  AND (bpresent = true OR NULLIF(TRIM(vchvalue), '') IS NOT NULL)
`;

export class PatientMedicalAllergySourceEntity extends BaseSourceEntity<PraktikaMedicalAllergy> {
  sourceEntity = PATIENT_MEDICAL_ALLERGY_SOURCE_ENTITY;
  entityResourceType = PATIENT_MEDICAL_ALLERGY_RESOURCE_TYPE;
  sourceQuery = PATIENT_MEDICAL_ALLERGY_SOURCE_QUERY;
  override estimateQuery = PATIENT_MEDICAL_ALLERGY_ESTIMATE_QUERY;
  verifySourceFn = isPraktikaMedicalAllergy;
  override transformDataFn = flow([]);

  translate(): object {
    return {};
  }

  getSourceRecordId(data: IPraktikaMedicalAllergy): string {
    return `${data.patient_id}-${data.item_id}`;
  }

  getSourceLabel(data: IPraktikaMedicalAllergy): string {
    return `${data.patient_id} - ${data.item_id}`;
  }

  getFilterData(
    data: IPraktikaMedicalAllergy,
    _timezone: Timezone
  ): IPraktikaMedicalAllergyFilters {
    return { patientId: data.patient_id };
  }
}
